import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Container, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios from 'axios';

// ─── E.164 phone number validation ──────────────────────────────────────────
const E164_REGEX = /^\+[1-9]\d{6,14}$/;

function SipTrunks(props) {
    const [trunks, setTrunks] = useState([]);
    const [showModal, setShowModal] = useState(false);

    // Form fields
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [transport, setTransport] = useState('');
    const [numbers, setNumbers] = useState('');
    const [mediaEncryption, setMediaEncryption] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Edit state
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editLivekitId, setEditLivekitId] = useState(null);

    // ── Test Call state ────────────────────────────────────────────────────
    const [testCallModal, setTestCallModal] = useState(false);
    const [testTrunk, setTestTrunk] = useState(null);           // full row data
    const [testPhoneNumber, setTestPhoneNumber] = useState('');
    const [testPhoneError, setTestPhoneError] = useState('');
    const [testCallStatus, setTestCallStatus] = useState('idle'); // idle | calling | success | error
    const [testCallMessage, setTestCallMessage] = useState('');
    // ──────────────────────────────────────────────────────────────────────

    useEffect(() => {
        fetchTrunks();
    }, []);

    const fetchTrunks = () => {
        const userId = props.uid || sessionStorage.getItem('userid');
        axios.get(`/campaigns/siptrunks/${userId}`)
            .then(res => setTrunks(res.data))
            .catch(err => console.error("Error fetching SIP trunks:", err));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const userId = props.uid || sessionStorage.getItem('userid');
        const payload = {
            userid: userId,
            name,
            trunk_type: 'Outbound', // Fixed to Outbound based on plan feedback
            address,
            transport,
            numbers,
            media_encryption_type: mediaEncryption,
            username,
            password
        };

        if (isEditing) {
            payload.livekit_id = editLivekitId;
            axios.put(`/campaigns/siptrunks/${editId}`, payload)
                .then(() => {
                    setShowModal(false);
                    fetchTrunks();
                    resetForm();
                })
                .catch(err => {
                    console.error("Error updating SIP trunk:", err);
                    alert("Failed to update SIP trunk.");
                })
                .finally(() => setIsSubmitting(false));
        } else {
            axios.post('/campaigns/siptrunks', payload)
                .then(() => {
                    setShowModal(false);
                    fetchTrunks();
                    resetForm();
                })
                .catch(err => {
                    console.error("Error creating SIP trunk:", err);
                    alert("Failed to create SIP trunk.");
                })
                .finally(() => setIsSubmitting(false));
        }
    };

    const handleEdit = (data) => {
        setIsEditing(true);
        setEditId(data.id);
        setEditLivekitId(data.livekit_id);
        setName(data.name || '');
        setAddress(data.address || '');
        setTransport(data.transport || '');
        setNumbers(data.numbers || '');
        setMediaEncryption(data.media_encryption_type || '');
        setUsername(data.username || '');
        setPassword(data.password || '');
        setShowModal(true);
    };

    const handleDelete = (id) => {
        if (!window.confirm("Are you sure you want to delete this SIP trunk?")) return;

        const userId = props.uid || sessionStorage.getItem('userid');
        axios.delete(`/campaigns/siptrunks/${id}/${userId}`)
            .then(() => fetchTrunks())
            .catch(err => console.error("Error deleting SIP trunk:", err));
    };

    const resetForm = () => {
        setIsEditing(false);
        setEditId(null);
        setEditLivekitId(null);
        setName('');
        setAddress('');
        setTransport('');
        setNumbers('');
        setMediaEncryption('');
        setUsername('');
        setPassword('');
    };

    // ── Test Call handlers ─────────────────────────────────────────────────
    const openTestCallModal = (rowData) => {
        setTestTrunk(rowData);
        setTestPhoneNumber('');
        setTestPhoneError('');
        setTestCallStatus('idle');
        setTestCallMessage('');
        setTestCallModal(true);
    };

    const closeTestCallModal = () => {
        setTestCallModal(false);
        setTestTrunk(null);
        setTestPhoneNumber('');
        setTestPhoneError('');
        setTestCallStatus('idle');
        setTestCallMessage('');
    };

    const validatePhone = (value) => {
        if (!value) return 'Phone number is required';
        if (!E164_REGEX.test(value)) return 'Enter a valid E.164 number, e.g. +919876543210';
        return '';
    };

    const handlePlaceTestCall = () => {
        const err = validatePhone(testPhoneNumber);
        if (err) { setTestPhoneError(err); return; }

        const userId = props.uid || sessionStorage.getItem('userid');
        setTestCallStatus('calling');
        setTestCallMessage('');

        axios.post(`/campaigns/siptrunks/${testTrunk.id}/test-call`, {
            userid: userId,
            phone_number: testPhoneNumber
        })
            .then(res => {
                setTestCallStatus('success');
                setTestCallMessage(
                    `Call placed successfully! Your phone should ring shortly. ` +
                    `The call will auto-end in 30 seconds. (Room: ${res.data.room})`
                );
            })
            .catch(err => {
                setTestCallStatus('error');
                const details = err.response?.data?.details || err.response?.data?.error || err.message;
                setTestCallMessage(`Test call failed: ${details}`);
            });
    };
    // ──────────────────────────────────────────────────────────────────────

    const columns = [
        { headerName: 'ID', field: 'id', width: 70 },
        { headerName: 'Name', field: 'name' },
        { headerName: 'Address', field: 'address' },
        { headerName: 'Transport', field: 'transport', width: 120 },
        { headerName: 'Numbers', field: 'numbers' },
        { headerName: 'Media Encryption', field: 'media_encryption_type' },
        { headerName: 'Username', field: 'username' },
        { headerName: 'Status', field: 'creation_status', width: 130 },
        {
            headerName: 'Actions',
            field: 'id',
            width: 230,
            minWidth: 230,
            suppressSizeToFit: true,
            cellRenderer: (params) => (
                <div style={{ whiteSpace: 'nowrap' }}>
                    <Button
                        variant="info"
                        size="sm"
                        className="me-1"
                        style={{ color: '#fff' }}
                        title="Place a real test call to verify this trunk"
                        disabled={params.data.creation_status !== 'Created'}
                        onClick={() => openTestCallModal(params.data)}
                    >
                        Test
                    </Button>
                    <Button variant="warning" size="sm" className="me-1" onClick={() => handleEdit(params.data)}>
                        Edit
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(params.value)}>
                        Delete
                    </Button>
                </div>
            )
        }
    ];

    return (
        <Container fluid className="mt-3">
            <Row className="mb-3">
                <Col>
                    <h3>SIP Trunks</h3>
                </Col>
                <Col className="text-end">
                    <Button variant="primary" onClick={() => { resetForm(); setShowModal(true); }}>Add SIP Trunk</Button>
                </Col>
            </Row>

            <div className="ag-theme-alpine" style={{ height: 'calc(100vh - 150px)', width: '100%' }}>
                <AgGridReact
                    rowData={trunks}
                    columnDefs={columns}
                    defaultColDef={{ flex: 1, resizable: true }}
                    pagination={true}
                    paginationPageSize={20}
                />
            </div>

            {/* ── Create / Edit Modal ───────────────────────────────────────── */}
            <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg">
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>{isEditing ? 'Edit SIP Trunk' : 'Create SIP Trunk'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name *</Form.Label>
                                    <Form.Control type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="My trunk" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Trunk Type</Form.Label>
                                    <Form.Control type="text" value="Outbound" disabled />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Address (Hostname or IP) *</Form.Label>
                                    <Form.Control type="text" required value={address} onChange={e => setAddress(e.target.value)} placeholder="192.0.2.0" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Transport</Form.Label>
                                    <Form.Select required value={transport} onChange={e => setTransport(e.target.value)}>
                                        <option value="" disabled>Select transport</option>
                                        <option value="Auto">Auto</option>
                                        <option value="UDP">UDP</option>
                                        <option value="TCP">TCP</option>
                                        <option value="TLS">TLS</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Numbers (Comma separated)</Form.Label>
                                    <Form.Control type="text" value={numbers} onChange={e => setNumbers(e.target.value)} placeholder="+15105550123,+15105550124" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Media Encryption Type</Form.Label>
                                    <Form.Select required value={mediaEncryption} onChange={e => setMediaEncryption(e.target.value)}>
                                        <option value="" disabled>Select media encryption</option>
                                        <option value="Media encryption Enabled">Media encryption Enabled</option>
                                        <option value="Media encryption Disabled">Media encryption Disabled</option>
                                        <option value="Media encryption Required">Media encryption Required</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Username</Form.Label>
                                    <Form.Control type="text" value={username} onChange={e => setUsername(e.target.value)} placeholder="Username" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Password</Form.Label>
                                    <Form.Control type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="$trong_password" />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Trunk' : 'Create Trunk')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* ── Test Call Modal ──────────────────────────────────────────── */}
            <Modal show={testCallModal} onHide={closeTestCallModal} size="md" centered>
                <Modal.Header closeButton closeVariant="white" className="bg-primary text-white" style={{ background: '#28749e', color: '#fff' }}>
                    <Modal.Title>
                        Test SIP Trunk
                        {testTrunk && (
                            <small className="d-block fw-normal" style={{ fontSize: '0.85rem', opacity: 0.85 }}>
                                {testTrunk.name} &mdash; {testTrunk.address}
                            </small>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                        Enter a phone number to receive the test call. Your AI agent will greet you and confirm
                        the trunk is working. The call auto-ends after <strong>30 seconds</strong>.
                    </p>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">Phone Number <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            type="tel"
                            placeholder="+919876543210"
                            value={testPhoneNumber}
                            onChange={e => {
                                setTestPhoneNumber(e.target.value);
                                if (testPhoneError) setTestPhoneError(validatePhone(e.target.value));
                            }}
                            isInvalid={!!testPhoneError}
                            disabled={testCallStatus === 'calling' || testCallStatus === 'success'}
                        />
                        <Form.Control.Feedback type="invalid">{testPhoneError}</Form.Control.Feedback>
                        <Form.Text className="text-muted">
                            Must be including country code, e.g. <code>+911234567890</code>
                        </Form.Text>
                    </Form.Group>

                    {/* Status feedback */}
                    {testCallStatus === 'calling' && (
                        <Alert variant="info" className="d-flex align-items-center gap-2 mb-0">
                            <Spinner animation="border" size="sm" />
                            <span>Placing call… your phone should ring in a moment.</span>
                        </Alert>
                    )}
                    {testCallStatus === 'success' && (
                        <Alert variant="success" className="mb-0">
                            {testCallMessage}
                        </Alert>
                    )}
                    {testCallStatus === 'error' && (
                        <Alert variant="danger" className="mb-0">
                            {testCallMessage}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeTestCallModal}>
                        Close
                    </Button>
                    {testCallStatus !== 'success' && (
                        <Button
                            variant="primary"
                            onClick={handlePlaceTestCall}
                            disabled={testCallStatus === 'calling'}
                        >
                            {testCallStatus === 'calling' ? (
                                <><Spinner animation="border" size="sm" className="me-2" />Calling…</>
                            ) : (
                                'Place Test Call'
                            )}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

export default SipTrunks;
